import { useState, useEffect, useRef } from 'preact/hooks';
import { scKeyframes } from '../components/skills-center/styles';
import { TabVerified } from '../components/skills-center/TabVerified';
import { TabRecommended } from '../components/skills-center/TabRecommended';
import { TabMarketplace } from '../components/skills-center/TabMarketplace';
import { TabInstalled } from '../components/skills-center/TabInstalled';
import { TabSettings } from '../components/skills-center/TabSettings';
import { SkillDetailPanel } from '../components/skills-center/SkillDetailPanel';
import { InstallModal } from '../components/skills-center/InstallModal';
import type { SkillMeta, TabId } from '../components/skills-center/types';
import { T } from '../styles/tokens';
import { Button } from '../components/ui';

const TABS: { id: TabId; label: string; icon: string }[] = [
 { id: 'verified', label: 'Verificadas', icon: '' },
 { id: 'recommended', label: 'Recomendadas', icon: '' },
 { id: 'marketplace', label: 'Marketplace', icon: '' },
 { id: 'installed', label: 'Instaladas', icon: '' },
 { id: 'settings', label: 'Configurações', icon: '' },
];

const SKILLS_TAB_IDS: TabId[] = ['verified', 'recommended', 'marketplace', 'installed', 'settings'];

export function SkillsCenterPage() {
  const initTab = (() => {
    const seg = window.location.pathname.replace(/^\/skills-center\/?/, '').split('/')[0];
    return SKILLS_TAB_IDS.includes(seg as TabId) ? seg as TabId : 'verified';
  })();
  const [activeTab, setActiveTabRaw] = useState<TabId>(initTab);
  const setActiveTab = (t: TabId) => {
    setActiveTabRaw(t);
    const newPath = t === 'verified' ? '/skills-center' : `/skills-center/${t}`;
    if (window.location.pathname !== newPath) window.history.replaceState(null, '', newPath);
  };
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [detailSkill, setDetailSkill] = useState<SkillMeta | null>(null);
  const [installSkill, setInstallSkill] = useState<SkillMeta | null>(null);
  const debounceRef = useRef<any>(null);

  // Animated counters
  const [counters, setCounters] = useState({ verified: 0, recommended: 0, total: 0 });
  useEffect(() => {
    const targets = { verified: 15, recommended: 12, total: 378 };
    const duration = 800;
    const steps = 30;
    let step = 0;
    const interval = setInterval(() => {
      step++;
      const progress = Math.min(step / steps, 1);
      const ease = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      setCounters({
        verified: Math.round(targets.verified * ease),
        recommended: Math.round(targets.recommended * ease),
        total: Math.round(targets.total * ease),
      });
      if (step >= steps) clearInterval(interval);
    }, duration / steps);
    return () => clearInterval(interval);
  }, []);

  // Debounce search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(search), 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [search]);

  return (
    <div style={{ padding: '0', maxWidth: '1200px', margin: '0 auto', animation: 'fadeInUp 0.5s ease' }}>
      <style>{scKeyframes}</style>

      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #7c3aed, #a855f7, #6d28d9)',
        borderRadius: '0 0 24px 24px', padding: '32px 28px 24px', marginBottom: '24px',
        position: 'relative', overflow: 'hidden',
      }}>
        {/* BG decoration */}
        <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '120px', height: '120px', borderRadius: '50%', background: T.border }} />
        <div style={{ position: 'absolute', bottom: '-30px', left: '40px', width: '80px', height: '80px', borderRadius: '50%', background: T.bgCardHover }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <h1 style={{ fontSize: '24px', fontWeight: 800, color: T.text, margin: '0 0 4px', letterSpacing: '-0.5px' }}>
 Skills Center
          </h1>
          <p style={{ fontSize: '14px', color: T.text, margin: '0 0 20px' }}>
            Descubra, instale e gerencie skills para seus agentes
          </p>

          {/* Counters */}
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            {[
              { label: 'Verificadas', value: counters.verified, color: T.success },
              { label: 'Recomendadas', value: counters.recommended, color: T.warning },
              { label: 'Total', value: `${counters.total}+`, color: T.primary },
            ].map(c => (
              <div key={c.label} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                  width: '10px', height: '10px', borderRadius: '50%', background: c.color,
                  boxShadow: `0 0 8px ${c.color}`,
                }} />
                <span style={{ fontSize: '20px', fontWeight: 800, color: T.text }}>{c.value}</span>
                <span style={{ fontSize: '12px', color: T.text }}>{c.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Search */}
      <div style={{ padding: '0 4px', marginBottom: '20px' }}>
        <div style={{ position: 'relative' }}>
 <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', color: T.textSecondary }}></span>
          <input
            type="text"
            placeholder="Buscar skills..."
            value={search}
            onInput={(e: any) => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '12px 14px 12px 42px', borderRadius: '12px',
              background: T.bgOverlay, border: `1px solid ${T.purpleBgStrong}`,
              color: T.text, fontSize: '14px', outline: 'none', boxSizing: 'border-box',
              backdropFilter: 'blur(16px)',
            }}
          />
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', padding: '0 4px', marginBottom: '24px', overflowX: 'auto' }}>
        {TABS.map(tab => (
          <Button variant="ghost"
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 18px', borderRadius: '10px',
              background: activeTab === tab.id ? 'linear-gradient(135deg, #7c3aed, #a855f7)' : T.bgOverlay,
              color: activeTab === tab.id ? '#fff' : T.textSecondary,
              fontSize: '13px', fontWeight: 600, cursor: 'pointer',
              transition: 'all 0.2s', whiteSpace: 'nowrap',
              border: activeTab === tab.id ? 'none' : `1px solid ${T.purpleBg}`,
            }}
          >
            {tab.icon} {tab.label}
          </Button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ padding: '0 4px' }}>
        {activeTab === 'verified' && <TabVerified search={debouncedSearch} onDetails={setDetailSkill} onInstall={setInstallSkill} />}
        {activeTab === 'recommended' && <TabRecommended search={debouncedSearch} onDetails={setDetailSkill} onInstall={setInstallSkill} />}
        {activeTab === 'marketplace' && <TabMarketplace search={debouncedSearch} onDetails={setDetailSkill} onInstall={setInstallSkill} />}
        {activeTab === 'installed' && <TabInstalled search={debouncedSearch} />}
        {activeTab === 'settings' && <TabSettings />}
      </div>

      {/* Detail panel */}
      {detailSkill && <SkillDetailPanel skill={detailSkill} onClose={() => setDetailSkill(null)} onInstall={(s) => { setDetailSkill(null); setInstallSkill(s); }} />}

      {/* Install modal */}
      {installSkill && <InstallModal skill={installSkill} onClose={() => setInstallSkill(null)} />}
    </div>
  );
}
